import { useCallback, useEffect, useRef, type RefObject, type TouchEvent as ReactTouchEvent } from 'react';

/** recharts 가 핸들러에 넘기는 터치 이벤트 — recharts 타입(`CategoricalChartFunc`)과 동일 형태. */
type ChartTouchEvent = ReactTouchEvent<SVGGraphicsElement>;

/**
 * recharts 외부 이벤트 핸들러가 받는 chart state(우리가 읽는 필드만) — `externalEventsMiddleware` 가 호출 시점의
 * 스토어에서 조립해 주입한다. `activeTooltipIndex` 가 `null` 이면 **툴팁이 떠 있지 않은 것**이다(닫히면 recharts
 * 가 좌표는 남기고 활성 index 는 비운다).
 */
export interface ChartPointerState {
  activeTooltipIndex?: number | string | null;
}

/** recharts 활성 카테고리 index → number|null. `null` = 툴팁 닫힘. */
function readActiveIndex(state: ChartPointerState | undefined): number | null {
  const idx = state?.activeTooltipIndex;
  if (idx == null || idx === '') return null;
  const n = Number(idx);
  return Number.isNaN(n) ? null : n;
}

/** 탭/드래그 구분 임계(px) — 이보다 많이 움직였으면 스크럽(드래그)으로 보고 토글하지 않는다. */
const TAP_SLOP_PX = 10;

/** 진행 중인 터치 제스처 — 시작 좌표(탭/드래그 판정)와 시작 시점의 활성 index(토글 기준). */
interface Gesture {
  x: number;
  y: number;
  wasIndex: number | null;
}

export interface ChartPointer {
  onTouchStart: (state: ChartPointerState, event: ChartTouchEvent) => void;
  onTouchEnd: (state: ChartPointerState, event: ChartTouchEvent) => void;
}

/**
 * 차트 터치 상호작용 — **데스크탑은 손대지 않고**, 터치에 "재탭/바깥탭으로 닫기"만 얹는다.
 *
 * recharts 툴팁은 `trigger='hover'` 라 닫히는 경로가 `mouseleave` 하나뿐이다. 터치에서는 손을 떼도 mouseleave 가
 * 안 나므로 툴팁이 계속 떠 있는다. 여는 것·드래그 추종은 recharts 가 이미 잘 하므로 **닫기만** 보탠다.
 *
 * ## 왜 `<Tooltip active>` 로 제어하지 않는가
 *
 * `active={false}` 는 툴팁의 **표시만** 막는다. recharts 내부 활성 index 는 그대로라 막대 dim·라인 activeDot 이
 * 남고(닫았는데 강조만 살아 있음), 그 상태를 우리 React state 로 흉내내면 소비처마다 예외를 덧대야 한다.
 * 그래서 제어 대신 **내부 상태를 실제로 비운다**(`closeTooltip`).
 *
 * ## 상태를 하나도 안 갖는다
 *
 * 이 훅은 `useState` 가 없다 — 열림 여부는 recharts 가 핸들러 인자로 주는 `activeTooltipIndex` 로 읽는다.
 * 제스처 도중 상위(`CartesianPlot`)가 재렌더되지 않는다는 뜻이고, 이게 **bar 드래그 스크럽의 전제**다:
 * 재렌더가 나면 `<Bar shape>` 가 다시 그려지며 touchstart 를 받은 DOM 노드가 갈리고, 이후 touchmove 가 wrapper
 * 까지 올라오지 못해 툴팁이 첫 위치에 얼어붙는다(line 은 이 경로가 없어 멀쩡했다). 활성 강조도 같은 이유로
 * 막대가 store 를 직접 구독한다(`chart-series.tsx` 의 `ActiveAwareBarRectangle`).
 *
 * ## 규칙
 * - **마우스**: 아무것도 안 한다(핸들러가 터치 전용). 기존 동작 불변.
 * - **드래그**: recharts 그대로(스크럽). 손을 떼도 열린 채 유지.
 * - **탭**: 닫혀 있었으면 recharts 가 연다 / 다른 카테고리면 그쪽으로 옮겨 열린다 / **같은 카테고리 재탭이면 닫는다**.
 * - **차트 바깥 탭**: 닫는다(`rootRef` 기준 containment).
 *
 * 한계(의도적): 터치 도중 `touchmove` 가 한 번도 안 뜬 초경량 탭이면 touchend 시점의 index 가 직전 값이라,
 * 열린 상태에서 **다른** 카테고리를 그렇게 탭하면 이동 대신 닫힌다(한 번 더 탭하면 그 자리에서 열림).
 *
 * @param rootRef 차트 컨테이너 ref — 바깥 탭 판정 영역이자 `.recharts-wrapper` 조회 기점.
 */
export function useChartPointer(rootRef: RefObject<HTMLDivElement | null>): ChartPointer {
  const gestureRef = useRef<Gesture | null>(null);

  /**
   * 툴팁 닫기 — recharts 내부 상태를 실제로 비운다. recharts 가 상태를 비우는 경로는 wrapper 의 `onMouseLeave`
   * (→ `mouseLeaveChart`) 하나뿐이라, React 가 그 합성 이벤트를 만들도록 **`mouseout`(relatedTarget = 차트 밖)** 을
   * 직접 쏜다. 마우스가 실제로 차트를 벗어난 것과 동일한 처리 → 툴팁·커서·activeDot·막대 dim 이 한꺼번에 정리된다.
   */
  const closeTooltip = useCallback(() => {
    const wrapper = rootRef.current?.querySelector('.recharts-wrapper');
    if (!(wrapper instanceof HTMLElement)) return;
    wrapper.dispatchEvent(
      new MouseEvent('mouseout', { bubbles: true, cancelable: true, relatedTarget: document.body }),
    );
  }, [rootRef]);

  // 차트 **바깥** 탭/클릭 → 닫기. capture 단계 — 바깥 요소가 stopPropagation 해도 우리가 먼저 받는다.
  // 차트 안쪽은 무시하고 `onTouchEnd` 의 토글 판정에 맡긴다. (마우스는 이미 mouseleave 로 닫혀 있어 무해.)
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const root = rootRef.current;
      if (!root) return; // containment 을 못 재면 닫지 않는다(fail-safe).
      const target = event.target;
      if (target instanceof Node && root.contains(target)) return;
      closeTooltip();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [rootRef, closeTooltip]);

  const onTouchStart = useCallback((state: ChartPointerState, event: ChartTouchEvent) => {
    const touch = event.touches[0];
    gestureRef.current = {
      x: touch?.clientX ?? 0,
      y: touch?.clientY ?? 0,
      wasIndex: readActiveIndex(state),
    };
  }, []);

  const onTouchEnd = useCallback(
    (state: ChartPointerState, event: ChartTouchEvent) => {
      const g = gestureRef.current;
      gestureRef.current = null;
      if (!g) return;
      // 이동량은 throttle 없는 touchstart↔touchend 좌표로만 판정(touchmove 는 rAF throttle 이라 순서를 못 믿는다).
      const touch = event.changedTouches[0];
      const moved = touch != null && Math.hypot(touch.clientX - g.x, touch.clientY - g.y) > TAP_SLOP_PX;
      if (moved) return; // 드래그(스크럽) — recharts 가 이미 추종 중. 손을 떼도 그대로 둔다.
      if (g.wasIndex == null) return; // 닫혀 있던 상태의 탭 = 열기 — recharts 가 알아서 연다.
      if (readActiveIndex(state) !== g.wasIndex) return; // 다른 카테고리로 옮겨 간 탭 — 열어 둔다.
      // 같은 카테고리 재탭 = 닫기. touchend 를 preventDefault 해 **뒤따르는 합성 마우스 이벤트를 막는다** —
      // 안 막으면 곧바로 오는 mousemove 가 같은 자리에서 다시 열어 토글이 성립하지 않는다.
      event.preventDefault();
      closeTooltip();
    },
    [closeTooltip],
  );

  return { onTouchStart, onTouchEnd };
}
